import { queryOptions } from "@tanstack/react-query";
import {
  fetchPrompts,
  fetchPrompt,
  fetchPromptVersions,
  fetchUsedInValues,
  fetchVariablesByUsedIn,
  fetchPromptLlmModels,
  type PromptsListFilters,
} from "@/features/prompts/api/prompts.api";

// ─── Key factory ─────────────────────────────────────────────────────

export const promptsKeys = {
  all: ["prompts"] as const,
  list: (filters?: PromptsListFilters) => ["prompts", "list", filters ?? {}] as const,
  detail: (id: string) => ["prompts", "detail", id] as const,
  versions: (id: string) => ["prompts", "versions", id] as const,
  usedInValues: ["prompts", "usedInValues"] as const,
  variables: (usedIn: string) => ["prompts", "variables", usedIn] as const,
  llmModels: ["prompts", "llmModels"] as const,
};

// ─── Query options ───────────────────────────────────────────────────

export function promptsListQueryOptions(filters?: PromptsListFilters) {
  return queryOptions({
    queryKey: promptsKeys.list(filters),
    queryFn: () => fetchPrompts(filters),
  });
}

export function promptDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: promptsKeys.detail(id),
    queryFn: () => fetchPrompt(id),
    enabled: !!id,
  });
}

export function promptVersionsQueryOptions(id: string) {
  return queryOptions({
    queryKey: promptsKeys.versions(id),
    queryFn: () => fetchPromptVersions(id),
    enabled: !!id,
  });
}

export function promptUsedInValuesQueryOptions() {
  return queryOptions({
    queryKey: promptsKeys.usedInValues,
    queryFn: () => fetchUsedInValues(),
    staleTime: 30_000,
  });
}

export function promptVariablesQueryOptions(usedIn: string) {
  return queryOptions({
    queryKey: promptsKeys.variables(usedIn),
    queryFn: () => fetchVariablesByUsedIn(usedIn),
    enabled: !!usedIn,
    staleTime: 30_000,
  });
}

export function promptsLlmModelsQueryOptions() {
  return queryOptions({
    queryKey: promptsKeys.llmModels,
    queryFn: () => fetchPromptLlmModels(),
    staleTime: 60_000,
  });
}
